import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { FaBolt, FaBrain, FaChevronLeft, FaChevronRight, FaCogs, FaDatabase, FaEye, FaMagic } from 'react-icons/fa';
import logo from '../../logo.png';
import { STAY_INFORMED_ITEMS, getStayInformedHref } from '../../constants/stayInformedLinks';
import { usePicksOfTheDay, useParlayOfTheDay } from '../../hooks/useApiQueries';
import { formatPickTypeLabel, formatPickValue } from '../../utils/formatPickType';
import PlayerWithAvatar from '../PlayerWithAvatar';
import TeamWithLogo from '../TeamWithLogo';
import usePageTitle from '../../hooks/usePageTitle';
import PageMeta from '../SEO/PageMeta';
import { buildModelPageDeepLink } from '../../utils/modelPathForPickType';
import ShapRadarChart from '../Predictions/ShapRadarChart';

/** Visual pipeline teaser steps for the homepage How it works section. */
const HOW_IT_WORKS_TEASER_STEPS = [
  {
    id: 'fetch',
    Icon: FaDatabase,
    title: 'Fetch',
    blurb: 'Live stats, lineups, and multi-book odds land every day.',
  },
  {
    id: 'engineer',
    Icon: FaCogs,
    title: 'Engineer',
    blurb: 'Proprietary features turn raw feeds into matchup signals.',
  },
  {
    id: 'predict',
    Icon: FaBrain,
    title: 'Predict',
    blurb: 'XGBoost models score each prop with a real probability.',
  },
  {
    id: 'explain',
    Icon: FaEye,
    title: 'Explain',
    blurb: 'SHAP values show exactly what drove the projection.',
  },
  {
    id: 'edge',
    Icon: FaBolt,
    title: 'Edge',
    blurb: 'Model odds vs the market — where value may exist.',
  },
  {
    id: 'curate',
    Icon: FaMagic,
    title: 'Curate',
    blurb: 'An LLM highlights Picks & Parlays of the Day for you.',
  },
];

/** Mini SHAP bars teased on the homepage (from a real Total Bases projection). */
const HOW_IT_WORKS_SHAP_TEASE = [
  { label: 'Career Splits', pct: 72, positive: true },
  { label: 'Expected Avg', pct: 46, positive: true },
  { label: 'Hot Streak', pct: 28, positive: true },
  { label: 'Weather', pct: 42, positive: false },
  { label: 'Park Factor', pct: 78, positive: false },
];

/** Copy for the “Why Choose SlickPicks?” carousel (order = scroll order). */
const WHY_CHOOSE_ITEMS = [
  {
    emoji: '💎',
    ariaLabel: 'Transparency',
    title: 'Transparency for Trust',
    description:
      "We show our winnings and we show our losses. Numbers don't lie. We share insights for our models and what is driving our predictions.",
  },
  {
    emoji: '🏟️',
    ariaLabel: 'MLB Experience',
    title: 'MLB Baseball Ops Experience',
    description:
      'Our lead engineer has previously worked in Baseball Operations for the Baltimore Orioles, bridging both scouting and analytics departments. We know how to measure talent.',
  },
  {
    emoji: '🔍',
    ariaLabel: 'Scout Program',
    title: 'MLB Scout Development Program Graduate',
    description:
      'Our lead engineer was sponsored to attend the MLB Scout Development Program, where he was taught how to evaluate talent and project performance by those who do it for a living.',
  },
  {
    emoji: '⚾',
    ariaLabel: 'Baseball Player',
    title: 'Former Division I Baseball Player',
    description:
      'Our lead engineer was a four year starter for a Division I baseball team. He knows what a good matchup looks like from personal experience.',
  },
  {
    emoji: '🎓',
    ariaLabel: 'MIT Graduate',
    title: 'MIT Applied Data Science Graduate',
    description:
      "Our lead engineer graduated from MIT's Applied Data Science program where he learned how to build machine learning models to make accurate predictions.",
  },
  {
    emoji: '🙋‍♂️',
    ariaLabel: 'Built for Us',
    title: 'Built for our own use',
    description:
      "We use our own models to make predictions for our own use. We're not trying to sell you a product, we're trying to share our edge.",
  },
];

/** Interval (ms) between automatic “Why choose” carousel advances. */
const WHY_CHOOSE_AUTO_MS = 5500;

const Hero = styled.div`
  background: linear-gradient(to right, #2b6cb0, #4299e1);
  padding: 2.5rem 1.5rem;
  color: white;
  text-align: center;
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 0.75rem;
  
  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  margin-bottom: 1.5rem;
  opacity: 0.9;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  
  @media (max-width: 640px) {
    flex-direction: column;
    gap: 0.5rem;
    align-items: center;
  }
`;

const Button = styled(Link)`
  display: inline-block;
  padding: 0.75rem 1.5rem;
  border-radius: 0.25rem;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s;
  
  &.primary {
    background-color: white;
    color: #2b6cb0;
    
    &:hover {
      background-color: #e2e8f0;
    }
  }
  
  &.secondary {
    background-color: transparent;
    border: 1px solid white;
    color: white;
    
    &:hover {
      background-color: rgba(255, 255, 255, 0.1);
    }
  }
`;

const FeaturesSection = styled.div`
  padding: 4rem 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2rem;
  color: ${props => props.theme.primaryText};
  margin-bottom: 1.75rem;
  transition: color 0.3s ease;
`;

const FeaturesCarouselOuter = styled.div`
  display: flex;
  align-items: stretch;
  gap: 0.75rem;
  position: relative;

  @media (min-width: 640px) {
    gap: 1rem;
  }
`;

const FeaturesCarouselTrack = styled.div`
  display: flex;
  align-items: stretch;
  gap: 1.25rem;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  scrollbar-width: thin;
  scrollbar-color: ${props => props.theme.border} transparent;
  padding: 0.25rem 0.25rem 1rem;
  flex: 1;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.border};
    border-radius: 3px;
  }
`;

const FeatureSlide = styled.div.attrs(() => ({
  'data-feature-slide': '',
}))`
  display: flex;
  flex-direction: column;
  flex: 0 0 clamp(260px, 82vw, 320px);
  scroll-snap-align: start;
  min-height: 0;
`;

const CarouselNavButton = styled.button`
  flex-shrink: 0;
  align-self: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  border: 1px solid ${props => props.theme.border};
  background: ${props => props.theme.cardBg};
  color: ${props => props.theme.primaryText};
  display: none;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s, opacity 0.2s, box-shadow 0.2s;
  box-shadow: ${props => props.theme.shadow};

  &:hover:not(:disabled) {
    background: ${props => props.theme.secondaryBg};
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  @media (min-width: 640px) {
    display: flex;
  }
`;

const FeatureCard = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  box-sizing: border-box;
  background-color: ${props => props.theme.cardBg};
  padding: 2rem;
  border-radius: 0.5rem;
  box-shadow: ${props => props.theme.shadow};
  text-align: center;
  transition: all 0.3s ease;
`;

const FeatureIcon = styled.div`
  background-color: #ebf8ff;
  color: #4299e1;
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  font-size: 1.5rem;
`;

const FeatureTitle = styled.h3`
  color: ${props => props.theme.primaryText};
  font-size: 1.25rem;
  margin-bottom: 1rem;
  transition: color 0.3s ease;
`;

const FeatureDescription = styled.p`
  flex: 1;
  margin: 0;
  color: ${props => props.theme.secondaryText};
  line-height: 1.5;
  transition: color 0.3s ease;
`;

const StayInformedSection = styled.section`
  padding: 2rem 1rem 2.5rem;
  max-width: 560px;
  margin: 0 auto;

  @media (min-width: 640px) {
    padding: 2.75rem 1.5rem 3.25rem;
    max-width: 640px;
  }
`;

const StayInformedIntro = styled.p`
  text-align: center;
  max-width: 26rem;
  margin: 0 auto 1.15rem;
  color: ${(props) => props.theme.secondaryText};
  line-height: 1.55;
  font-size: 0.9375rem;

  @media (min-width: 640px) {
    margin-bottom: 1.35rem;
    font-size: 1rem;
    max-width: 32rem;
  }
`;

/** Single stacked list — compact on mobile, no multi-column card grid. */
const StayInformedList = styled.div`
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 0.5rem;
  overflow: hidden;
  background: ${(props) => props.theme.cardBg};
  box-shadow: ${(props) => props.theme.shadow};
`;

const stayInformedRowStyles = css`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.6rem 0.75rem;
  text-decoration: none;
  color: inherit;
  transition: background 0.15s ease;

  &:not(:first-child) {
    border-top: 1px solid ${(props) => props.theme.border};
  }

  &:hover {
    background: ${(props) => props.theme.secondaryBg};
  }

  &:focus-visible {
    outline: 2px solid ${(props) => props.$accent};
    outline-offset: -2px;
    z-index: 1;
    position: relative;
  }

  @media (min-width: 640px) {
    padding: 0.7rem 0.9rem;
    gap: 0.85rem;
  }
`;

const StayInformedLinkRow = styled(Link)`
  ${stayInformedRowStyles}
`;

const StayInformedExternalRow = styled.a`
  ${stayInformedRowStyles}
`;

const StayInformedIconWrap = styled.div`
  flex-shrink: 0;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  background: color-mix(in srgb, ${(props) => props.$accent} 18%, transparent);
  color: ${(props) => props.$accent};

  @media (min-width: 640px) {
    width: 2.4rem;
    height: 2.4rem;
  }
`;

const StayInformedRowText = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  text-align: left;
`;

const StayInformedRowTitle = styled.span`
  font-weight: 600;
  font-size: 0.9rem;
  color: ${(props) => props.theme.primaryText};
  line-height: 1.25;

  @media (min-width: 640px) {
    font-size: 0.9375rem;
  }
`;

const StayInformedRowSubtitle = styled.span`
  font-size: 0.75rem;
  line-height: 1.35;
  color: ${(props) => props.theme.secondaryText};

  @media (min-width: 640px) {
    font-size: 0.8125rem;
  }
`;

const StayInformedRowChevron = styled.span`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  color: ${(props) => props.theme.mutedText};
  font-size: 0.7rem;
  opacity: 0.65;
`;

const StayInformedHeading = styled(SectionTitle)`
  margin-bottom: 1rem;

  @media (min-width: 640px) {
    margin-bottom: 1.2rem;
  }
`;

const CtaSection = styled.div`
  background-color: ${props => props.theme.secondaryBg};
  padding: 4rem 1.5rem;
  text-align: center;
  transition: background-color 0.3s ease;
`;

const CtaContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const CtaTitle = styled.h2`
  font-size: 2rem;
  color: ${props => props.theme.primaryText};
  margin-bottom: 1rem;
  transition: color 0.3s ease;
`;

const CtaText = styled.p`
  color: ${props => props.theme.secondaryText};
  margin-bottom: 2rem;
  font-size: 1.125rem;
  transition: color 0.3s ease;
`;

const CurrentPredictions = styled.div`
  padding: 3rem 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const HowItWorksTeaser = styled.section`
  padding: 4rem 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const HowItWorksIntro = styled.p`
  text-align: center;
  color: ${(props) => props.theme.secondaryText};
  font-size: 1.1rem;
  line-height: 1.6;
  max-width: 680px;
  margin: -0.5rem auto 2rem;
`;

const PipelineGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 0.75rem;
  margin-bottom: 1.75rem;

  @media (max-width: 960px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const pipelinePulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(66, 153, 225, 0.35); }
  50% { box-shadow: 0 0 0 8px rgba(66, 153, 225, 0); }
`;

const PipelineStepCard = styled.div`
  background: ${(props) => props.theme.cardBg};
  border: 1px solid ${(props) => (props.$active ? props.theme.primary : props.theme.border)};
  border-radius: 0.75rem;
  padding: 1.1rem 0.85rem;
  text-align: center;
  box-shadow: ${(props) => props.theme.shadow};
  transition: transform 0.25s ease, border-color 0.25s ease;
  animation: ${(props) => (props.$active ? pipelinePulse : 'none')} 2s ease infinite;
  cursor: default;

  &:hover {
    transform: translateY(-4px);
    border-color: ${(props) => props.theme.primary};
  }
`;

const PipelineStepIcon = styled.div`
  width: 2.75rem;
  height: 2.75rem;
  margin: 0 auto 0.65rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => (props.$active ? props.theme.primary : '#ebf8ff')};
  color: ${(props) => (props.$active ? '#fff' : '#4299e1')};
  font-size: 1.05rem;
  transition: background 0.25s ease, color 0.25s ease;
`;

const PipelineStepTitle = styled.div`
  font-weight: 700;
  font-size: 0.95rem;
  color: ${(props) => props.theme.primaryText};
  margin-bottom: 0.35rem;
`;

const PipelineStepBlurb = styled.p`
  font-size: 0.78rem;
  line-height: 1.4;
  color: ${(props) => props.theme.mutedText};
  margin: 0;
`;

const TeaserVisualRow = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 1.25rem;
  margin-bottom: 1.75rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TeaserPanel = styled.div`
  background: ${(props) => props.theme.cardBg};
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 0.75rem;
  padding: 1.25rem 1.35rem;
  box-shadow: ${(props) => props.theme.shadow};
`;

const TeaserPanelTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${(props) => props.theme.primaryText};
  margin: 0 0 0.35rem;
`;

const TeaserPanelSub = styled.p`
  font-size: 0.8rem;
  color: ${(props) => props.theme.mutedText};
  margin: 0 0 1rem;
  line-height: 1.4;
`;

const ShapTeaseRow = styled.div`
  display: grid;
  grid-template-columns: 88px 1fr 36px;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.55rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ShapTeaseLabel = styled.span`
  font-size: 0.72rem;
  color: ${(props) => props.theme.secondaryText};
  text-align: right;
`;

const ShapTeaseTrack = styled.div`
  height: 8px;
  border-radius: 999px;
  background: ${(props) => props.theme.tertiaryBg || '#4a5568'};
  overflow: hidden;
`;

const shapGrow = keyframes`
  from { width: 0; }
  to { width: var(--bar-width); }
`;

const ShapTeaseFill = styled.div`
  height: 100%;
  width: var(--bar-width);
  border-radius: 999px;
  background: ${(props) => (props.$positive ? props.theme.success : props.theme.error)};
  animation: ${shapGrow} 0.9s ease both;
  animation-delay: ${(props) => props.$delay};
`;

const ShapTeaseSign = styled.span`
  font-size: 0.7rem;
  font-weight: 700;
  color: ${(props) => (props.$positive ? props.theme.success : props.theme.error)};
`;

const EdgeTeaseBars = styled.div`
  display: grid;
  gap: 0.85rem;
`;

const EdgeTeaseLabel = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  color: ${(props) => props.theme.secondaryText};
  margin-bottom: 0.35rem;
`;

const EdgeTeaseTrack = styled.div`
  height: 10px;
  border-radius: 999px;
  background: ${(props) => props.theme.tertiaryBg || '#4a5568'};
  overflow: hidden;
`;

const EdgeTeaseFill = styled.div`
  height: 100%;
  width: var(--bar-width);
  border-radius: 999px;
  background: ${(props) => props.$color};
  animation: ${shapGrow} 0.9s ease both;
  animation-delay: ${(props) => props.$delay};
`;

const EdgeTeaseCallout = styled.div`
  margin-top: 0.25rem;
  text-align: center;
  font-size: 0.9rem;
  font-weight: 700;
  color: ${(props) => props.theme.success};
  padding: 0.65rem 0.75rem;
  border-radius: 0.5rem;
  background: rgba(72, 187, 120, 0.12);
  border: 1px solid rgba(72, 187, 120, 0.35);
`;

const HowItWorksCtaWrap = styled.div`
  text-align: center;
`;

const TabbedCard = styled.div`
  background-color: ${props => props.theme.cardBg};
  border-radius: 0.5rem;
  box-shadow: ${props => props.theme.shadow};
  overflow: hidden;
  margin-top: 2rem;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: row;

  @media (max-width: 768px) {
    flex-direction: row;
  }
`;

const TabSidebar = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.secondaryBg};
  border-right: 1px solid ${props => props.theme.border};
  padding: 0.75rem 0.5rem;
  gap: 0.25rem;
  min-width: 150px;

  @media (max-width: 640px) {
    min-width: 120px;
  }
`;

const TabRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.25rem;
  width: 100%;
`;

const TabButton = styled.button`
  padding: 0.55rem 0.75rem;
  font-size: 0.9rem;
  font-weight: 500;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  background: ${props => props.$active ? props.theme.primary : 'transparent'};
  color: ${props => props.$active ? 'white' : props.theme.primaryText};
  transition: all 0.2s ease;
  text-align: left;
  width: 100%;
  &:hover {
    background: ${props => props.$active ? props.theme.primaryHover : props.theme.tertiaryBg};
    color: ${props => props.$active ? 'white' : props.theme.primaryText};
  }
  @media (max-width: 640px) {
    font-size: 0.8rem;
    padding: 0.45rem 0.6rem;
  }
`;

const TabContent = styled.div`
  padding: 1.25rem 1.5rem 1.5rem;
  flex: 1;
`;

const TabContentTitle = styled.h3`
  margin: 0 0 0.75rem 0;
  font-size: 1.25rem;
  color: ${props => props.theme.primaryText};
  transition: color 0.3s ease;
`;

const TabContentDescription = styled.p`
  color: ${props => props.theme.secondaryText};
  margin-bottom: 1.5rem;
  line-height: 1.5;
  transition: color 0.3s ease;
`;

const CardButton = styled(Link)`
  display: inline-block;
  padding: 0.75rem 1.5rem;
  background-color: #4299e1;
  color: white;
  border-radius: 0.25rem;
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    background-color: #3182ce;
  }
`;


const PicksOfTheDaySection = styled.section`
  padding: 2rem 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const PicksOfTheDaySectionTitle = styled.h2`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  text-align: center;
  font-size: 2rem;
  color: ${props => props.theme.primaryText};
  margin-bottom: 3rem;
  transition: color 0.3s ease;
  .sparkle {
    color: #4299e1;
    font-size: 1.35rem;
    flex-shrink: 0;
    line-height: 1;
  }
`;

const PicksOfTheDayCard = styled.div`
  background: ${props => props.theme.cardBg};
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-bottom: 1rem;
  box-shadow: ${props => props.theme.shadow};
  border-left: 4px solid #4299e1;
  @media (max-width: 600px) {
    padding: 1rem;
  }
`;

const PicksOfTheDayList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const PicksOfTheDayItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.85rem 0;
  border-bottom: 1px solid ${props => props.theme.border};
  color: ${props => props.theme.primaryText};
  &:last-child { border-bottom: none; }
  @media (max-width: 600px) {
    padding: 0.6rem 0;
    gap: 0.25rem;
  }
`;

const PickRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  width: 100%;
  min-width: 0;
  text-align: center;
  @media (max-width: 600px) {
    gap: 0.6rem;
  }
`;

const PickRowAvatar = styled.span`
  flex-shrink: 0;
  align-self: center;
`;

const PickNameAndPrediction = styled.span`
  display: inline;
  white-space: nowrap;
  line-height: 1.5;
  font-size: inherit;
  @media (max-width: 600px) {
    white-space: normal;
  }
`;

const PickBlurb = styled.div`
  width: 100%;
  font-size: 0.9rem;
  color: ${props => props.theme.secondaryText};
  line-height: 1.4;
  hyphens: none;
  word-break: normal;
  overflow-wrap: break-word;
  padding-left: 0;
  @media (max-width: 600px) {
    font-size: 0.85rem;
    line-height: 1.35;
  }
`;

const PickShapWrap = styled.div`
  width: 100%;
  margin-top: 0.5rem;
`;

const PickDetailsLink = styled(Link)`
  font-size: 0.85rem;
  color: ${props => props.theme.primary};
  margin-top: 0.25rem;
  display: block;
  text-align: center;
  &:hover { text-decoration: underline; }
`;

/* Parlay of the Day sub-section (only when one exists) */
const ParlaySubsectionTitle = styled.h3`
  font-size: 1.25rem;
  color: ${props => props.theme.primaryText};
  margin: 2rem 0 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${props => props.theme.border};
  transition: color 0.3s ease;
`;

const ParlayCard = styled.div`
  background: ${props => props.theme.secondaryBg || 'rgba(255,255,255,0.04)'};
  border-radius: 0.5rem;
  padding: 1.25rem;
  margin-top: 0.5rem;
  border-left: 4px solid #38a169;
  box-shadow: ${props => props.theme.shadow};
`;

const ParlayBlurb = styled.p`
  font-size: 0.95rem;
  color: ${props => props.theme.secondaryText};
  line-height: 1.45;
  margin-bottom: 1rem;
`;

const ParlayMatchup = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${props => props.theme.primaryText};
  margin-bottom: 0.75rem;
`;

const ParlayLegs = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 0.75rem;
`;

const ParlayLegItem = styled.li`
  font-size: 0.9rem;
  color: ${props => props.theme.primaryText};
  padding: 0.35rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ParlayOddsRow = styled.div`
  font-size: 0.9rem;
  color: ${props => props.theme.secondaryText};
  margin-top: 0.5rem;
  padding-top: 0.75rem;
  border-top: 1px solid ${props => props.theme.border};
`;

/* Favored team TB edge on the parlay card (e.g. "+4.2 TB") */
const ParlayTbDelta = styled.span`
  color: #38a169;
  font-weight: 600;
`;

const PREDICTION_MODELS = [
  {
    id: 'game-picks',
    title: 'Game Picks',
    description: 'AI-powered favorable picks per game for both teams—TB, HR, hit, and 2+ hit picks by game.',
    to: '/game-picks',
    label: 'View Game Picks',
  },
  {
    id: 'tb',
    title: 'MLB Total Bases',
    description: "Our machine learning model provides predictions for which players are most likely to record 2+ total bases in today's games.",
    to: '/mlb/total-bases',
    label: 'View TB Predictions',
  },
  {
    id: 'hit',
    title: 'MLB Hit',
    description: "Our model predicts the probability each player gets at least one hit in today's games.",
    to: '/mlb/hit',
    label: 'View Hit Predictions',
  },
  {
    id: 'two-hits',
    title: 'MLB 2+ Hits',
    description: "Our model predicts the probability each player records 2 or more hits in today's games.",
    to: '/mlb/two-hits',
    label: 'View 2+ Hit Predictions',
  },
  {
    id: 'hr',
    title: 'MLB Home Runs',
    description: "Our machine learning model provides predictions for which players are most likely to hit a home run in today's games.",
    to: '/mlb/hr',
    label: 'View HR Predictions',
  },
];

const HomePage = () => {
  const { isAuthenticated } = useAuth();
  const { theme } = useTheme();
  usePageTitle('MLB Player Prop Predictions & Betting Tools');
  const [activeModelIndex, setActiveModelIndex] = useState(0);
  const [activePipelineStep, setActivePipelineStep] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActivePipelineStep((prev) => (prev + 1) % HOW_IT_WORKS_TEASER_STEPS.length);
    }, 2800);
    return () => clearInterval(timer);
  }, []);

  // Current day in Eastern time (stable across re-renders)
  const dateStr = useMemo(
    () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }),
    [],
  );

  const { data: picksOfTheDayRaw } = usePicksOfTheDay(dateStr);
  const { data: parlayRaw } = useParlayOfTheDay(dateStr);

  const picksOfTheDay = picksOfTheDayRaw ?? null;
  const parlaysOfTheDay = useMemo(() => {
    const raw = parlayRaw?.parlays;
    if (Array.isArray(raw)) {
      return raw.filter(
        (p) => p?.game_id && Array.isArray(p?.legs) && p.legs.length > 0,
      );
    }
    if (
      parlayRaw?.game_id &&
      Array.isArray(parlayRaw?.legs) &&
      parlayRaw.legs.length > 0
    ) {
      return [parlayRaw];
    }
    return [];
  }, [parlayRaw]);

  const hasPicks = picksOfTheDay?.picks_of_the_day?.length > 0;
  const hasParlayOfTheDay = parlaysOfTheDay.length > 0;
  const showPicksSection = hasPicks || hasParlayOfTheDay;
  const activeModel = PREDICTION_MODELS[activeModelIndex];

  const featuresScrollRef = useRef(null);
  const featuresCarouselRootRef = useRef(null);
  const whyChoosePausedRef = useRef(false);
  const reduceMotionRef = useRef(false);

  const [canScrollFeaturesLeft, setCanScrollFeaturesLeft] = useState(false);
  const [canScrollFeaturesRight, setCanScrollFeaturesRight] = useState(true);

  const updateFeaturesScrollState = useCallback(() => {
    const el = featuresScrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScroll = scrollWidth - clientWidth;
    setCanScrollFeaturesLeft(scrollLeft > 4);
    setCanScrollFeaturesRight(maxScroll > 4 && scrollLeft < maxScroll - 4);
  }, []);

  useEffect(() => {
    updateFeaturesScrollState();
    const el = featuresScrollRef.current;
    if (!el) return undefined;
    const onScroll = () => updateFeaturesScrollState();
    el.addEventListener('scroll', onScroll, { passive: true });
    const ro = new ResizeObserver(() => updateFeaturesScrollState());
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', onScroll);
      ro.disconnect();
    };
  }, [updateFeaturesScrollState]);

  const advanceWhyChoose = useCallback(() => {
    const el = featuresScrollRef.current;
    if (!el || whyChoosePausedRef.current) return;
    const slides = el.querySelectorAll('[data-feature-slide]');
    if (slides.length <= 1) return;
    let idx = 0;
    const left = el.scrollLeft;
    slides.forEach((slide, i) => {
      if (slide.offsetLeft <= left + 8) idx = i;
    });
    const next = (idx + 1) % slides.length;
    const behavior = reduceMotionRef.current ? 'auto' : 'smooth';
    el.scrollTo({ left: slides[next].offsetLeft, behavior });
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    let intervalId;
    const sync = () => {
      reduceMotionRef.current = mq.matches;
      if (intervalId) {
        window.clearInterval(intervalId);
        intervalId = undefined;
      }
      if (!mq.matches) {
        intervalId = window.setInterval(advanceWhyChoose, WHY_CHOOSE_AUTO_MS);
      }
    };
    sync();
    mq.addEventListener('change', sync);
    return () => {
      if (intervalId) window.clearInterval(intervalId);
      mq.removeEventListener('change', sync);
    };
  }, [advanceWhyChoose]);

  const pauseWhyChooseAuto = useCallback(() => {
    whyChoosePausedRef.current = true;
  }, []);

  const resumeWhyChooseAutoIfUnfocused = useCallback(() => {
    const root = featuresCarouselRootRef.current;
    if (root && document.activeElement && root.contains(document.activeElement)) {
      return;
    }
    whyChoosePausedRef.current = false;
  }, []);

  const handleWhyChooseBlurCapture = useCallback(
    (e) => {
      if (!e.currentTarget.contains(e.relatedTarget)) {
        resumeWhyChooseAutoIfUnfocused();
      }
    },
    [resumeWhyChooseAutoIfUnfocused],
  );

  const scrollWhyChoose = (direction) => {
    const el = featuresScrollRef.current;
    if (!el) return;
    const firstSlide = el.querySelector('[data-feature-slide]');
    const slideW = firstSlide ? firstSlide.getBoundingClientRect().width : 300;
    const behavior = reduceMotionRef.current ? 'auto' : 'smooth';
    el.scrollBy({ left: direction * (slideW + 20), behavior });
  };

  return (
    <>
      <PageMeta description="Daily MLB player prop predictions, model-driven picks of the day, and curated game picks powered by Slick Picks' explainable machine learning models." />
      <Hero>
        <HeroContent>
          <img src={logo} alt="SlickPicks Logo" style={{ height: '64px', marginBottom: '1rem' }} />
          <HeroTitle>Slick Picks</HeroTitle>
          <HeroSubtitle>
          FREE Sports betting picks powered by AI, built by a sports guy. 
          </HeroSubtitle>
          {isAuthenticated && (
            <ButtonGroup>
              <Button to="/dashboard" className="primary">Go to Dashboard</Button>
            </ButtonGroup>
          )}
        </HeroContent>
      </Hero>

      {showPicksSection && (
        <PicksOfTheDaySection>
          <PicksOfTheDaySectionTitle theme={theme}>
            Picks of the Day
            <span className="sparkle" title="AI-selected picks" role="img" aria-label="AI-selected">✨</span>
          </PicksOfTheDaySectionTitle>
          <PicksOfTheDayCard theme={theme}>
            {hasPicks && (
            <PicksOfTheDayList>
              {picksOfTheDay.picks_of_the_day.map((pick) => (
                <PicksOfTheDayItem key={pick.rank} theme={theme}>
                  <PickRow>
                    <PickRowAvatar>
                      <PlayerWithAvatar
                        playerId={pick.player_id ?? pick.playerId}
                        playerName={pick.player_name ?? pick.playerName}
                        size={28}
                        showName={false}
                      />
                    </PickRowAvatar>
                    <PickNameAndPrediction>
                      <strong>{pick.player_name ?? pick.playerName ?? '—'}</strong>
                      {' — '}
                      {formatPickTypeLabel(pick.pick_type)}
                      {pick.prediction_value != null && (
                        <>
                          {'\u00A0'}
                          ({formatPickValue(pick.pick_type, pick.prediction_value)})
                        </>
                      )}
                    </PickNameAndPrediction>
                  </PickRow>
                  {pick.blurb && (
                    <PickBlurb theme={theme}>{pick.blurb}</PickBlurb>
                  )}
                  {pick.shap?.feature_names?.length > 0 && (
                    <PickShapWrap>
                      <ShapRadarChart
                        shapData={pick.shap}
                        playerName={pick.player_name ?? pick.playerName}
                        forceBarLayout
                        compact
                        compactMaxFeatures={6}
                        labelMaxLength={28}
                        maxWidth={360}
                      />
                    </PickShapWrap>
                  )}
                  <PickDetailsLink
                    theme={theme}
                    to={buildModelPageDeepLink({
                      pickType: pick.pick_type,
                      playerName: pick.player_name ?? pick.playerName,
                      date: picksOfTheDay?.date,
                    })}
                  >
                    View details on {formatPickTypeLabel(pick.pick_type)} page →
                  </PickDetailsLink>
                </PicksOfTheDayItem>
              ))}
            </PicksOfTheDayList>
            )}
            {hasParlayOfTheDay && (
              <>
                <ParlaySubsectionTitle theme={theme}>
                  {parlaysOfTheDay.length > 1 ? 'Parlays of the Day' : 'Parlay of the Day'}
                </ParlaySubsectionTitle>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem',
                  }}
                >
                  {parlaysOfTheDay.map((parlay) => (
                    <ParlayCard key={parlay.game_id} theme={theme}>
                      {parlay.blurb && (
                        <ParlayBlurb theme={theme}>{parlay.blurb}</ParlayBlurb>
                      )}
                      {(parlay.away_team || parlay.home_team) && (
                        <ParlayMatchup
                          theme={theme}
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: '0.4rem',
                          }}
                        >
                          <TeamWithLogo
                            teamId={parlay.away_team_id}
                            teamName={parlay.away_team ?? 'Away'}
                            size={22}
                          />
                          <span style={{ color: theme?.secondaryText }}>@</span>
                          <TeamWithLogo
                            teamId={parlay.home_team_id}
                            teamName={parlay.home_team ?? 'Home'}
                            size={22}
                          />
                        </ParlayMatchup>
                      )}
                      <ParlayLegs>
                        {parlay.legs.map((leg, idx) => {
                          const isMoneyline = leg.type !== 'player_prop';
                          let tbDelta = null;
                          if (
                            isMoneyline &&
                            parlay.favored_team != null &&
                            parlay.away_team != null &&
                            parlay.home_team != null &&
                            typeof parlay.away_total_tb === 'number' &&
                            typeof parlay.home_total_tb === 'number'
                          ) {
                            const fav = String(parlay.favored_team).trim();
                            const away = String(parlay.away_team).trim();
                            const home = String(parlay.home_team).trim();
                            if (fav === away) {
                              tbDelta = parlay.away_total_tb - parlay.home_total_tb;
                            } else if (fav === home) {
                              tbDelta = parlay.home_total_tb - parlay.away_total_tb;
                            }
                          }
                          return (
                            <ParlayLegItem key={idx} theme={theme}>
                              {leg.type === 'player_prop' ? (
                                <>
                                  <PlayerWithAvatar
                                    playerId={leg.player_id ?? leg.playerId}
                                    playerName={leg.player_name}
                                    size={26}
                                    showName={false}
                                  />
                                  <span style={{ flex: 1, minWidth: 0 }}>
                                    <strong>{leg.player_name}</strong>
                                    {' — '}
                                    {formatPickTypeLabel(leg.pick_type)}
                                    {leg.prediction_value != null &&
                                      ` (${formatPickValue(leg.pick_type, leg.prediction_value)})`}
                                    {leg.odds_american != null && (
                                      <span
                                        style={{
                                          color: theme?.secondaryText,
                                          fontWeight: 'normal',
                                        }}
                                      >
                                        {' '}
                                        {leg.odds_american >= 0
                                          ? `+${leg.odds_american}`
                                          : leg.odds_american}
                                      </span>
                                    )}
                                  </span>
                                </>
                              ) : (
                                <>
                                  <TeamWithLogo
                                    teamId={leg.team_id}
                                    teamName={leg.team ?? leg.description ?? 'Team'}
                                    size={24}
                                    showName={false}
                                  />
                                  <span style={{ flex: 1, minWidth: 0 }}>
                                    <strong>{leg.team ?? leg.description}</strong>
                                    {tbDelta != null ? (
                                      <ParlayTbDelta theme={theme}>
                                        {' '}
                                        {tbDelta >= 0 ? '+' : ''}
                                        {tbDelta.toFixed(1)} TB
                                      </ParlayTbDelta>
                                    ) : null}
                                  </span>
                                </>
                              )}
                            </ParlayLegItem>
                          );
                        })}
                      </ParlayLegs>
                      {(parlay.parlay_odds_american != null ||
                        parlay.to_win_per_10 != null) && (
                        <ParlayOddsRow theme={theme}>
                          {parlay.parlay_odds_american != null && (
                            <span>
                              Parlay odds:{' '}
                              {parlay.parlay_odds_american >= 0
                                ? `+${parlay.parlay_odds_american}`
                                : parlay.parlay_odds_american}
                            </span>
                          )}
                          {parlay.to_win_per_10 != null && (
                            <span style={{ marginLeft: '1rem' }}>
                              $10 pays ${parlay.to_win_per_10.toFixed(2)}
                            </span>
                          )}
                          {parlay.parlay_note && (
                            <div
                              style={{ marginTop: '0.35rem', fontSize: '0.85em' }}
                            >
                              {parlay.parlay_note}
                            </div>
                          )}
                        </ParlayOddsRow>
                      )}
                    </ParlayCard>
                  ))}
                </div>
              </>
            )}
          </PicksOfTheDayCard>
        </PicksOfTheDaySection>
      )}

      <CurrentPredictions>
        <SectionTitle theme={theme}>Today's Predictions</SectionTitle>
        <TabbedCard theme={theme}>
          <TabSidebar theme={theme}>
            {PREDICTION_MODELS.map((model, index) => (
              <TabButton
                key={model.id}
                theme={theme}
                $active={activeModelIndex === index}
                onClick={() => setActiveModelIndex(index)}
                type="button"
              >
                {model.title}
              </TabButton>
            ))}
          </TabSidebar>
          <TabContent>
            <TabContentTitle theme={theme}>{activeModel.title}</TabContentTitle>
            <TabContentDescription theme={theme}>{activeModel.description}</TabContentDescription>
            <CardButton to={activeModel.to}>{activeModel.label}</CardButton>
          </TabContent>
        </TabbedCard>
      </CurrentPredictions>

      <HowItWorksTeaser aria-labelledby="how-it-works-heading">
        <SectionTitle id="how-it-works-heading" theme={theme}>
          How it works
        </SectionTitle>
        <HowItWorksIntro theme={theme}>
          Not a ChatGPT tip of the day — a living pipeline of proprietary features,
          XGBoost models, SHAP explanations, and live odds.
        </HowItWorksIntro>

        <PipelineGrid>
          {HOW_IT_WORKS_TEASER_STEPS.map((step, index) => {
            const Icon = step.Icon;
            const isActive = index === activePipelineStep;
            return (
              <PipelineStepCard
                key={step.id}
                theme={theme}
                $active={isActive}
                onMouseEnter={() => setActivePipelineStep(index)}
              >
                <PipelineStepIcon theme={theme} $active={isActive}>
                  <Icon aria-hidden />
                </PipelineStepIcon>
                <PipelineStepTitle theme={theme}>{step.title}</PipelineStepTitle>
                <PipelineStepBlurb theme={theme}>{step.blurb}</PipelineStepBlurb>
              </PipelineStepCard>
            );
          })}
        </PipelineGrid>

        <TeaserVisualRow>
          <TeaserPanel theme={theme}>
            <TeaserPanelTitle theme={theme}>SHAP transparency</TeaserPanelTitle>
            <TeaserPanelSub theme={theme}>
              See which features lifted or cut a projection — same insights on live picks.
            </TeaserPanelSub>
            {HOW_IT_WORKS_SHAP_TEASE.map((row, index) => (
              <ShapTeaseRow key={row.label}>
                <ShapTeaseLabel theme={theme}>{row.label}</ShapTeaseLabel>
                <ShapTeaseTrack theme={theme}>
                  <ShapTeaseFill
                    theme={theme}
                    $positive={row.positive}
                    $delay={`${0.08 * index}s`}
                    style={{ '--bar-width': `${row.pct}%` }}
                  />
                </ShapTeaseTrack>
                <ShapTeaseSign theme={theme} $positive={row.positive}>
                  {row.positive ? '+' : '−'}
                </ShapTeaseSign>
              </ShapTeaseRow>
            ))}
          </TeaserPanel>

          <TeaserPanel theme={theme}>
            <TeaserPanelTitle theme={theme}>Model vs market</TeaserPanelTitle>
            <TeaserPanelSub theme={theme}>
              When our probability beats the books&apos; implied chance, that gap is potential edge.
            </TeaserPanelSub>
            <EdgeTeaseBars>
              <div>
                <EdgeTeaseLabel theme={theme}>
                  <span>Model probability</span>
                  <span>58%</span>
                </EdgeTeaseLabel>
                <EdgeTeaseTrack theme={theme}>
                  <EdgeTeaseFill
                    $color={theme.primary}
                    $delay="0.1s"
                    style={{ '--bar-width': '58%' }}
                  />
                </EdgeTeaseTrack>
              </div>
              <div>
                <EdgeTeaseLabel theme={theme}>
                  <span>Market implied</span>
                  <span>47%</span>
                </EdgeTeaseLabel>
                <EdgeTeaseTrack theme={theme}>
                  <EdgeTeaseFill
                    $color={theme.warning}
                    $delay="0.25s"
                    style={{ '--bar-width': '47%' }}
                  />
                </EdgeTeaseTrack>
              </div>
              <EdgeTeaseCallout theme={theme}>+11 pts estimated edge</EdgeTeaseCallout>
            </EdgeTeaseBars>
          </TeaserPanel>
        </TeaserVisualRow>

        <HowItWorksCtaWrap>
          <Button to="/how-it-works" className="primary" style={{ backgroundColor: '#4299e1', color: 'white' }}>
            Explore the full pipeline
          </Button>
        </HowItWorksCtaWrap>
      </HowItWorksTeaser>
      
      <FeaturesSection>
        <SectionTitle theme={theme}>Why Choose SlickPicks?</SectionTitle>
        <FeaturesCarouselOuter
          ref={featuresCarouselRootRef}
          onMouseEnter={pauseWhyChooseAuto}
          onMouseLeave={resumeWhyChooseAutoIfUnfocused}
          onFocusCapture={pauseWhyChooseAuto}
          onBlurCapture={handleWhyChooseBlurCapture}
        >
          <CarouselNavButton
            type="button"
            theme={theme}
            aria-label="Previous highlight"
            disabled={!canScrollFeaturesLeft}
            onClick={() => scrollWhyChoose(-1)}
          >
            <FaChevronLeft aria-hidden />
          </CarouselNavButton>
          <FeaturesCarouselTrack
            ref={featuresScrollRef}
            theme={theme}
            role="region"
            aria-roledescription="carousel"
            aria-label="Why choose SlickPicks highlights"
          >
            {WHY_CHOOSE_ITEMS.map((item) => (
              <FeatureSlide key={item.title}>
                <FeatureCard theme={theme}>
                  <FeatureIcon>
                    <span role="img" aria-label={item.ariaLabel}>
                      {item.emoji}
                    </span>
                  </FeatureIcon>
                  <FeatureTitle theme={theme}>{item.title}</FeatureTitle>
                  <FeatureDescription theme={theme}>{item.description}</FeatureDescription>
                </FeatureCard>
              </FeatureSlide>
            ))}
          </FeaturesCarouselTrack>
          <CarouselNavButton
            type="button"
            theme={theme}
            aria-label="Next highlight"
            disabled={!canScrollFeaturesRight}
            onClick={() => scrollWhyChoose(1)}
          >
            <FaChevronRight aria-hidden />
          </CarouselNavButton>
        </FeaturesCarouselOuter>
      </FeaturesSection>

      <StayInformedSection aria-labelledby="stay-informed-heading">
        <StayInformedHeading id="stay-informed-heading" theme={theme}>
          Stay in the loop
        </StayInformedHeading>
        <StayInformedIntro theme={theme}>
          Don&apos;t miss a pick — follow below or turn on email in your free account.
        </StayInformedIntro>
        <StayInformedList theme={theme}>
          {STAY_INFORMED_ITEMS.map((item) => {
            const href = getStayInformedHref(item, isAuthenticated);
            const Icon = item.Icon;
            const inner = (
              <>
                <StayInformedIconWrap $accent={item.accent}>
                  <Icon aria-hidden size={17} />
                </StayInformedIconWrap>
                <StayInformedRowText>
                  <StayInformedRowTitle theme={theme}>{item.title}</StayInformedRowTitle>
                  <StayInformedRowSubtitle theme={theme}>{item.subtitle}</StayInformedRowSubtitle>
                </StayInformedRowText>
                <StayInformedRowChevron theme={theme} aria-hidden>
                  <FaChevronRight />
                </StayInformedRowChevron>
              </>
            );
            return item.external ? (
              <StayInformedExternalRow
                key={item.key}
                href={href}
                theme={theme}
                $accent={item.accent}
                target="_blank"
                rel="noopener noreferrer"
              >
                {inner}
              </StayInformedExternalRow>
            ) : (
              <StayInformedLinkRow key={item.key} to={href} theme={theme} $accent={item.accent}>
                {inner}
              </StayInformedLinkRow>
            );
          })}
        </StayInformedList>
      </StayInformedSection>

      <CtaSection theme={theme}>
        <CtaContent>
          <CtaTitle theme={theme}>Ready to make smarter bets?</CtaTitle>
          <CtaText theme={theme}>
            Start using SlickPicks today and leverage the power of machine learning for your sports betting.
          </CtaText>
          {isAuthenticated ? (
            <Button to="/dashboard" className="primary" style={{ backgroundColor: '#4299e1', color: 'white' }}>
              Go to Dashboard
            </Button>
          ) : (
            <Button to="/register" className="primary" style={{ backgroundColor: '#4299e1', color: 'white' }}>
              Sign Up for Free
            </Button>
          )}
        </CtaContent>
      </CtaSection>
    </>
  );
};

export default HomePage; 